import React, { FC, useMemo } from 'react';
import { createContext } from '@shared/utils/createContext';
import { GuestSiteEventDesign } from '../layout.types';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useTheme } from '@withjoy/joykit';
import { deriveColorPreferencesFromEventDesign } from '@shared/utils/websiteDesign';
import { DesignLayoutType } from '@graphql/generated';

type ColorPaletteProviderContext = {
  accentColor?: string;
  artBackgroundColor?: string;
  pageFontColor?: string;
  pageColor?: string;
  artBackgroundFontColor?: string;
  rsvpButtonColor?: string;
};

type ColorPaletteProviderProps = {
  eventDesign: GuestSiteEventDesign;
};

const [Provider, useColorPaletteProvider] = createContext<ColorPaletteProviderContext>({
  name: 'ColorPaletteProvider'
});

const ColorPaletteProvider: FC<ColorPaletteProviderProps> = ({ children, eventDesign }) => {
  const theme = useTheme();
  const isGuestSiteColorsExperimentEnabled = useFeatureValue('guestSiteColorsExperiment').value === 'treatment';

  const ctx = useMemo<ColorPaletteProviderContext>(() => {
    if (isGuestSiteColorsExperimentEnabled && eventDesign) {
      const { primaryFillColor, primaryTextColor, customArtBackgroundColor, pageColor, pageFontColor, consolidatedBackgroundColorHex } = deriveColorPreferencesFromEventDesign(
        eventDesign,
        eventDesign.theme.colorSchemes
      );
      const rsvpButtonColor = eventDesign.websiteLayout.layoutType === DesignLayoutType.aloha ? primaryTextColor?.color.hex : primaryFillColor?.color.hex;

      return {
        accentColor: primaryFillColor?.color.hex,

        // if customArtBackgroundColor is not set, use the consolidated background color
        // This scenario is possible when we enable the feature flag but the user is no longer editing their website design
        // and does not have a custom background color set.
        artBackgroundColor: customArtBackgroundColor?.color?.hex || consolidatedBackgroundColorHex || theme.colors.white,
        artBackgroundFontColor: primaryTextColor?.color.hex || theme.colors.black,
        pageColor: pageColor?.color.hex || theme.colors.white,
        pageFontColor: pageFontColor?.color.hex || theme.colors.black,
        rsvpButtonColor: rsvpButtonColor || theme.colors.black
      };
    }

    return {
      accentColor: undefined,
      artBackgroundColor: undefined,
      artBackgroundFontColor: undefined,
      pageFontColor: undefined,
      pageColor: undefined
    };
  }, [isGuestSiteColorsExperimentEnabled, eventDesign, theme]);

  return <Provider value={ctx}>{children}</Provider>;
};

export { ColorPaletteProvider, useColorPaletteProvider };
